import React, { useId, useRef } from 'react'
import { cn } from '../../lib/cn'

export interface UnderlineTab<T extends string> {
  key: T
  label: string
  icon?: React.ReactNode
  count?: number
  disabled?: boolean
  /** Точка рядом с подписью: у вкладки есть проблема (warn — внимание, bad — ошибка). */
  attention?: 'warn' | 'bad'
}

export interface UnderlineTabsProps<T extends string> {
  tabs: Array<UnderlineTab<T>>
  active: T
  onChange: (key: T) => void
  className?: string
  ariaLabel?: string
  panelId?: string
}

/**
 * UnderlineTabs — generic underline-style tab bar.
 * Generalised from `DesignsModeTabs`. Use for sub-views inside a page/drawer.
 */
export function UnderlineTabs<T extends string>({
  tabs,
  active,
  onChange,
  className,
  ariaLabel = 'Вкладки',
  panelId,
}: UnderlineTabsProps<T>) {
  const generatedId = useId().replace(/:/g, '')
  const tabsId = `underline-tabs-${generatedId}`
  const tabsRef = useRef<Array<HTMLButtonElement | null>>([])

  const moveFocus = (currentIndex: number, direction: 1 | -1 | 'first' | 'last') => {
    const enabledIndexes = tabs
      .map((tab, index) => (!tab.disabled ? index : -1))
      .filter((index) => index >= 0)
    if (enabledIndexes.length === 0) return

    const currentEnabledIndex = Math.max(0, enabledIndexes.indexOf(currentIndex))
    const nextIndex =
      direction === 'first'
        ? enabledIndexes[0]
        : direction === 'last'
          ? enabledIndexes[enabledIndexes.length - 1]
          : enabledIndexes[
              (currentEnabledIndex + direction + enabledIndexes.length) % enabledIndexes.length
            ]

    tabsRef.current[nextIndex]?.focus()
    onChange(tabs[nextIndex].key)
  }

  return (
    <div
      className={cn(
        // `overflow-y-hidden` обязателен рядом с `overflow-x-auto`: CSS вычисляет
        // оставшуюся `visible` ось в `auto`, и подчёркивание вкладок (`-mb-px`)
        // вылезает за контентную коробку на пиксель — этого хватает на
        // вертикальную полосу прокрутки у правого края ленты.
        'flex items-center gap-1 px-1 bg-white border-b border-gray-200 overflow-x-auto overflow-y-hidden dark:border-zinc-800 dark:bg-zinc-900',
        className,
      )}
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
    >
      {tabs.map((t, index) => {
        const isActive = active === t.key
        return (
          <button
            ref={(node) => {
              tabsRef.current[index] = node
            }}
            id={`${tabsId}-${t.key}`}
            key={t.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={panelId}
            tabIndex={isActive ? 0 : -1}
            disabled={t.disabled}
            onClick={() => !t.disabled && onChange(t.key)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault()
                moveFocus(index, 1)
              } else if (event.key === 'ArrowLeft') {
                event.preventDefault()
                moveFocus(index, -1)
              } else if (event.key === 'Home') {
                event.preventDefault()
                moveFocus(index, 'first')
              } else if (event.key === 'End') {
                event.preventDefault()
                moveFocus(index, 'last')
              }
            }}
            className={cn(
              'inline-flex items-center gap-2 h-10 px-3 -mb-px text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              isActive
                ? 'border-indigo-600 text-indigo-700 dark:text-indigo-400'
                : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-zinc-100',
              t.disabled && 'opacity-40 cursor-not-allowed',
            )}
          >
            {t.icon}
            {t.label}
            {t.attention && (
              <span
                aria-hidden="true"
                data-attention={t.attention}
                className={cn(
                  'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
                  t.attention === 'bad' ? 'bg-red-500 dark:bg-red-400' : 'bg-amber-500 dark:bg-amber-400',
                )}
              />
            )}
            {typeof t.count === 'number' && (
              <span
                className={cn(
                  'ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-2xs font-semibold rounded-full tabular-nums',
                  isActive
                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-300',
                )}
              >
                {t.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export default UnderlineTabs
